import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faMagnifyingGlass,
  faArrowUpShortWide,
  faArrowDownWideShort,
} from "@fortawesome/free-solid-svg-icons";
import TriageCategoryStrip from "./TriageCategoryStrip";
import TriageQueueTable from "./TriageQueueTable";
import AssignFollowUpPanel from "./AssignFollowUpPanel";
import CloseCasePanel from "./CloseCasePanel";
import { useCareHubData, normalizeCareHubRange } from "../../hooks/useCareHubData";
import {
  assignCareTriageCase,
  startCareTriageCase,
  postCareTriageMessage,
  closeCareTriageCase,
  reopenCareTriageCase,
  markCareTriageRead,
} from "../../utils/api";
import { exportTriageCasesCsv, exportTriageCasesPdf } from "../../utils/careTriageExport";
import {
  TRIAGE_BUCKET_META,
  countTriageWorkflow,
  countActiveAssignmentCases,
  filterActiveAssignmentCases,
  filterTriageByBucket,
  filterTriageWorkflow,
  sortTriageRows,
  TRIAGE_SORT_COLUMNS,
} from "../../utils/careTriageModel";
import "./CareTriagePage.css";

const TRIAGE_TABS = [
  { id: "new", label: "New" },
  { id: "assigned", label: "Assigned / In Progress" },
  { id: "overdue", label: "Overdue" },
  { id: "resolved", label: "Resolved / History" },
];

const ASSIGNED_TABS = [
  { id: "assigned", label: "Assigned / In Progress" },
  { id: "overdue", label: "Overdue" },
  { id: "resolved", label: "Resolved / History" },
];

const ASSIGNED_PAGE_META = {
  title: "Assigned Cases",
  subtitle:
    "Active follow-up assigned to nurses and care team members. Review progress, communicate with supervisors, and close cases when complete.",
  tone: "blue",
};

const CareTriagePage = (bannerProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAssignedView =
    bannerProps.careHubView === "assigned" || pathname === "/care/assigned-cases";

  const bucket = isAssignedView ? "all" : searchParams.get("bucket") || "action";
  const defaultTab = isAssignedView ? "assigned" : "new";
  const tabParam =
    searchParams.get("tab") || (searchParams.get("view") === "resolved" ? "resolved" : "");
  const activeTab = (isAssignedView ? ASSIGNED_TABS : TRIAGE_TABS).some((t) => t.id === tabParam)
    ? tabParam
    : defaultTab;
  const queueView = activeTab === "resolved" ? "resolved" : "active";
  const qParam = searchParams.get("q") || "";
  const changeRange = normalizeCareHubRange(searchParams.get("range"));

  const [query, setQuery] = useState(qParam);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sort, setSort] = useState({ key: "detected", dir: "desc" });
  const [assignRow, setAssignRow] = useState(null);
  const [closeRow, setCloseRow] = useState(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [resolverFilter, setResolverFilter] = useState("all");
  const [workflowError, setWorkflowError] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    setQuery(qParam);
  }, [qParam]);

  const locationId = String(
    bannerProps.activeLocation?._id || bannerProps.activeLocation?.id || "",
  ).trim();

  const { loading, error, summary, triageCases, changeMeta, reloadWorkflows } = useCareHubData(
    bannerProps.activeLocation,
    bannerProps.locations,
    changeRange,
  );

  const buildParams = ({ tab = activeTab, range = changeRange } = {}) => {
    const params = {};
    if (!isAssignedView && bucket !== "action") params.bucket = bucket;
    if (tab !== defaultTab) params.tab = tab;
    if (qParam) params.q = qParam;
    params.range = normalizeCareHubRange(range);
    return params;
  };

  const setChangeRange = (nextRange) => setSearchParams(buildParams({ range: nextRange }));
  const setActiveTab = (tab) => setSearchParams(buildParams({ tab }));

  const bucketMeta = isAssignedView
    ? ASSIGNED_PAGE_META
    : TRIAGE_BUCKET_META[bucket] || TRIAGE_BUCKET_META.action;

  const scopedCases = useMemo(() => {
    let list = isAssignedView
      ? filterActiveAssignmentCases(triageCases)
      : filterTriageByBucket(triageCases, bucket);

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          String(r.clientName || "").toLowerCase().includes(q) ||
          String(r.clientId || "").toLowerCase().includes(q) ||
          String(r.clientShortId || "").toLowerCase().includes(q) ||
          String(r.note || "").toLowerCase().includes(q),
      );
    }
    if (sourceFilter !== "all") {
      list = list.filter((r) => r.sourceType === sourceFilter);
    }
    if (ownerFilter !== "all") {
      list = list.filter((r) => String(r.owner || "") === ownerFilter);
    }
    if (queueView === "resolved" && resolverFilter !== "all") {
      list = list.filter((r) => String(r.resolvedBy || "") === resolverFilter);
    }
    if (reviewFilter === "ready_for_review") {
      list = list.filter((r) => r.reviewStatus === "ready_for_review");
    }
    if (reviewFilter === "follow_up_needed") {
      list = list.filter((r) => r.reviewStatus === "follow_up_needed");
    }
    return sortTriageRows(list, sort.key, sort.dir);
  }, [
    triageCases,
    bucket,
    query,
    sourceFilter,
    ownerFilter,
    reviewFilter,
    resolverFilter,
    queueView,
    sort,
    isAssignedView,
  ]);

  const ownerOptions = useMemo(() => {
    const set = new Set();
    triageCases.forEach((r) => {
      const o = String(r.owner || "").trim();
      if (o) set.add(o);
    });
    return [...set].sort();
  }, [triageCases]);

  const resolverOptions = useMemo(() => {
    const set = new Set();
    triageCases.forEach((r) => {
      const o = String(r.resolvedBy || "").trim();
      if (o) set.add(o);
    });
    return [...set].sort();
  }, [triageCases]);

  const reviewSummary = useMemo(() => {
    const open = filterActiveAssignmentCases(scopedCases);
    return {
      ready: open.filter((r) => r.reviewStatus === "ready_for_review").length,
      followUp: open.filter((r) => r.reviewStatus === "follow_up_needed").length,
    };
  }, [scopedCases]);

  const workflowCounts = useMemo(() => {
    if (isAssignedView) {
      const c = countActiveAssignmentCases(triageCases);
      const resolved = filterTriageWorkflow(triageCases, "resolved").length;
      return {
        assigned: c.assigned,
        overdue: c.overdue,
        resolved,
        new: 0,
      };
    }
    return countTriageWorkflow(scopedCases);
  }, [isAssignedView, scopedCases, triageCases]);

  const displaySummary = useMemo(() => {
    if (isAssignedView || !summary) return summary;
    const c = countTriageWorkflow(scopedCases);
    const openTotal = c.new + c.assigned + c.overdue;
    if (bucket === "action") {
      return {
        ...summary,
        action: {
          total: openTotal,
          newCount: c.new,
          assignedCount: c.assigned,
          overdueCount: c.overdue,
        },
      };
    }
    if (bucket === "monitor") {
      return {
        ...summary,
        monitor: {
          total: openTotal,
          newCount: c.new,
          assignedCount: c.assigned,
          overdueCount: c.overdue,
        },
      };
    }
    if (bucket === "stable") {
      return { ...summary, stable: { total: scopedCases.length } };
    }
    if (bucket === "review") {
      return { ...summary, reviewData: { total: scopedCases.length } };
    }
    return summary;
  }, [summary, scopedCases, bucket, isAssignedView]);

  const workflowGroups = useMemo(() => {
    const resolvedPool = isAssignedView ? triageCases : scopedCases;
    return {
      new: filterTriageWorkflow(scopedCases, "new"),
      assigned: filterTriageWorkflow(scopedCases, "assigned"),
      overdue: filterTriageWorkflow(scopedCases, "overdue"),
      resolved: filterTriageWorkflow(resolvedPool, "resolved"),
    };
  }, [scopedCases, triageCases, isAssignedView]);

  const resolvedSummary = useMemo(() => {
    const rows = workflowGroups.resolved || [];
    const by = (re) => rows.filter((r) => re.test(r.reviewResult || "Approved and Closed")).length;
    return [
      { id: "total", label: "Total Resolved", value: rows.length, tone: "blue" },
      { id: "approved", label: "Approved & Closed", value: by(/approved/i), tone: "green" },
      { id: "followup", label: "Closed After Follow-Up", value: by(/follow-up/i), tone: "amber" },
      { id: "nfa", label: "No Further Action", value: by(/no further/i), tone: "gray" },
    ];
  }, [workflowGroups]);

  const tabs = isAssignedView ? ASSIGNED_TABS : TRIAGE_TABS;

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: TRIAGE_SORT_COLUMNS[key]?.firstDir || "asc" },
    );
  };

  // Resolved At only exists in the resolved view; fall back when leaving it.
  useEffect(() => {
    if (queueView !== "resolved" && sort.key === "resolvedAt") {
      setSort({ key: "detected", dir: "desc" });
    }
  }, [queueView, sort.key]);

  const sortOptions = Object.entries(TRIAGE_SORT_COLUMNS).filter(
    ([key]) => key !== "resolvedAt" || queueView === "resolved",
  );

  const handleAssign = async ({ assignTo, assignToUserId, actionType, due, priority, notes }) => {
    if (!assignRow || !locationId) return;
    setAssignSaving(true);
    setWorkflowError("");
    try {
      await assignCareTriageCase({
        eventKey: assignRow.id,
        locationId,
        clientId: assignRow.clientId,
        triageBucket: assignRow.bucket,
        assignTo,
        assignToUserId,
        actionType,
        dueDate: due || null,
        priority,
        notes,
        detectedAt: assignRow.changeDate,
        eventSnapshot: {
          clientName: assignRow.clientName,
          note: assignRow.note,
          change: assignRow.change,
          previousScore: assignRow.aipPrevious,
          currentScore: assignRow.aipCurrent,
          source: assignRow.source,
        },
      });
      await reloadWorkflows();
      setAssignRow(null);
    } catch (err) {
      setWorkflowError(err?.response?.data?.message || "Failed to assign case");
    } finally {
      setAssignSaving(false);
    }
  };

  const handleRowExpand = async (row) => {
    if (!locationId) return;
    const role = String(bannerProps.currentUser?.role || "").toLowerCase();
    const supervisor = ["admin", "superadmin", "owner", "manager", "dps", "supervisor"].some(
      (r) => role.includes(r),
    );
    // Only the assignee opening the case counts as "work started".
    if (row.workflowStatus === "assigned" && !supervisor) {
      try {
        await startCareTriageCase(row.eventKey || row.id, locationId);
        await reloadWorkflows();
      } catch {
        /* optional */
      }
    }
    try {
      await markCareTriageRead(row.eventKey || row.id, {
        locationId,
        audience: supervisor ? "supervisor" : "assignee",
      });
      await reloadWorkflows();
    } catch {
      /* ignore */
    }
  };

  const handleSubmitMessage = async (row, messageType, text) => {
    if (!locationId) return;
    setWorkflowError("");
    try {
      await postCareTriageMessage(row.eventKey || row.id, {
        locationId,
        messageType,
        text,
      });
      await reloadWorkflows();
    } catch (err) {
      setWorkflowError(err?.response?.data?.message || "Failed to send message");
    }
  };

  const handleCloseCase = async (payload) => {
    if (!closeRow || !locationId) return;
    setWorkflowError("");
    try {
      await closeCareTriageCase(closeRow.row.eventKey || closeRow.row.id, {
        locationId,
        ...payload,
      });
      await reloadWorkflows();
      setCloseRow(null);
    } catch (err) {
      setWorkflowError(err?.response?.data?.message || "Failed to close case");
    }
  };

  const handleReopenCase = async (row, reason) => {
    if (!locationId || !reason) return;
    setWorkflowError("");
    try {
      await reopenCareTriageCase(row.eventKey || row.id, { locationId, reason });
      await reloadWorkflows();
    } catch (err) {
      setWorkflowError(err?.response?.data?.message || "Failed to reopen case");
    }
  };

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onDown = (e) => {
      if (!exportRef.current?.contains(e.target)) setExportOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setExportOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [exportOpen]);

  const handleExport = (format) => {
    setExportOpen(false);
    const sections = tabs.map(({ id, label }) => ({ id, label, rows: workflowGroups[id] || [] }));
    const base = isAssignedView ? `assigned-cases-${changeRange}` : `triage-${bucket}-${changeRange}`;
    if (format === "pdf") {
      exportTriageCasesPdf(sections, {
        title: bucketMeta.title,
        subtitle: bucketMeta.subtitle,
        filename: `${base}.pdf`,
      });
    } else {
      exportTriageCasesCsv(sections, `${base}.csv`);
    }
  };

  const triageReturnTo = () => {
    const base = isAssignedView ? "/care/assigned-cases" : "/care/triage";
    return `${base}?${new URLSearchParams(buildParams()).toString()}`;
  };

  const openClient = (row) => {
    if (!row?.clientId) return;
    navigate(`/client-management/client/${row.clientId}/aip-analysis`, {
      state: { careHubReturnTo: triageReturnTo(), careHubReturnLabel: "Back to Triage" },
    });
  };

  // Layer 5 opens already loaded with this client (no re-selecting).
  const openSimulator = (row) => {
    if (!row?.clientId) return;
    navigate(`/aip-smart-care/client/${row.clientId}`, {
      state: { careHubReturnTo: triageReturnTo(), careHubReturnLabel: "Back to Triage Event" },
    });
  };

  return (
    <div className={`care-triage-page${isAssignedView ? " care-triage-page--assigned" : ""}`}>
      {loading ? (
        <div className="care-hub-loading">Loading triage…</div>
      ) : (
        <>
          {!isAssignedView ? (
            <TriageCategoryStrip summary={displaySummary} activeBucket={bucket} />
          ) : null}

          <nav className="care-triage-page__backs">
            <Link to="/care-dashboard" className="care-triage-page__back">
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Dashboard
            </Link>
            {isAssignedView || bucket !== "all" ? (
              <Link to="/care/triage?bucket=all" className="care-triage-page__back">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to All Triage Cases
              </Link>
            ) : null}
          </nav>

          <header className={`care-triage-page__head care-triage-page__head--${bucketMeta.tone}`}>
            <div>
              <h1>
                <span className="care-triage-page__dot" aria-hidden="true" />
                {bucketMeta.title}
              </h1>
              <p>{bucketMeta.subtitle}</p>
            </div>
            <div className="care-triage-export-wrap" ref={exportRef}>
              <button
                type="button"
                className="care-triage-export"
                aria-haspopup="menu"
                aria-expanded={exportOpen}
                onClick={() => setExportOpen((o) => !o)}
              >
                Export
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
              {exportOpen ? (
                <div className="care-triage-export-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => handleExport("csv")}>
                    Export as .csv
                  </button>
                  <button type="button" role="menuitem" onClick={() => handleExport("pdf")}>
                    Export as .pdf
                  </button>
                </div>
              ) : null}
            </div>
          </header>

          {error ? (
            <div className="care-hub-alert care-hub-alert--error" role="alert">
              {error}
            </div>
          ) : null}
          {workflowError ? (
            <div className="care-hub-alert care-hub-alert--error" role="alert">
              {workflowError}
            </div>
          ) : null}
          {!error && !loading && changeMeta ? (
            <p className="care-triage-data-note" aria-live="polite">
              Showing real AIP score changes from the last{" "}
              {changeRange === "today"
                ? "24 hours"
                : changeRange === "month"
                  ? "30 days"
                  : "7 days"}
              {changeMeta.locationScope === "unity-flemington-princeton"
                ? " (Unity Flemington + Princeton clients combined)"
                : changeMeta.locationScope === "company"
                  ? " (all locations in agency)"
                  : ""}
              .
              {triageCases.length === 0
                ? " No clients in this location scope — check Client Management or your active site."
                : ""}
            </p>
          ) : null}

          <div className="care-triage-page__panel">
            <div
              className={`care-triage-tabs care-triage-tabs--${bucketMeta.tone}`}
              role="tablist"
              aria-label="Workflow status"
            >
              {tabs.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === id}
                  className={`care-triage-tab-v2${activeTab === id ? " is-active" : ""}`}
                  onClick={() => setActiveTab(id)}
                >
                  {label} ({workflowCounts[id] ?? 0})
                </button>
              ))}
            </div>
            {(activeTab === "assigned" || activeTab === "overdue") &&
              (isAssignedView || reviewSummary.ready > 0 || reviewSummary.followUp > 0) && (
              <div className="care-triage-review-filters">
                <button
                  type="button"
                  className={`care-triage-review-chip${reviewFilter === "all" ? " is-active" : ""}`}
                  onClick={() => setReviewFilter("all")}
                >
                  All open
                </button>
                <button
                  type="button"
                  className={`care-triage-review-chip${reviewFilter === "ready_for_review" ? " is-active" : ""}`}
                  onClick={() => setReviewFilter("ready_for_review")}
                >
                  {reviewSummary.ready} Ready for Review
                </button>
                <button
                  type="button"
                  className={`care-triage-review-chip${reviewFilter === "follow_up_needed" ? " is-active" : ""}`}
                  onClick={() => setReviewFilter("follow_up_needed")}
                >
                  {reviewSummary.followUp} Follow-Up Needed
                </button>
              </div>
            )}

            {queueView === "resolved" ? (
              <div className="care-triage-resolved-stats">
                {resolvedSummary.map((st) => (
                  <div key={st.id} className={`care-triage-resolved-stat care-triage-resolved-stat--${st.tone}`}>
                    <strong>{st.value}</strong>
                    <span>{st.label}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="care-triage-toolbar-v2">
              <div className="care-triage-search-wrap">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="care-triage-search-icon" />
                <input
                  type="search"
                  className="care-triage-search-v2"
                  placeholder="Search by client name, ID, or keyword…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <select
                className="care-triage-select"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                aria-label="Filter by source"
              >
                <option value="all">All Sources</option>
                <option value="caregiver">Caregiver AI Call</option>
                <option value="nurse">Nurse Assessment</option>
                <option value="device">PacSana</option>
                <option value="sensi">Sensi</option>
                <option value="aip">AIP assessment</option>
                <option value="able">Able Care Device</option>
              </select>
              <select
                className="care-triage-select"
                aria-label="Filter by owner"
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
              >
                <option value="all">All Owners</option>
                {ownerOptions.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              {queueView === "resolved" ? (
                <select
                  className="care-triage-select"
                  aria-label="Filter by resolver"
                  value={resolverFilter}
                  onChange={(e) => setResolverFilter(e.target.value)}
                >
                  <option value="all">All Resolvers</option>
                  {resolverOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : null}
              <select
                className="care-triage-select"
                aria-label="Date range"
                value={changeRange}
                onChange={(e) => setChangeRange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="week">Past 7 days</option>
                <option value="month">Past 30 days</option>
              </select>
              <div className="care-triage-sort">
                <label htmlFor="care-triage-sort-key">Sort by:</label>
                <select
                  id="care-triage-sort-key"
                  className="care-triage-sort__select"
                  value={sort.key}
                  onChange={(e) => handleSort(e.target.value)}
                >
                  {sortOptions.map(([key, col]) => (
                    <option key={key} value={key}>
                      {col.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="care-triage-sort__dir"
                  onClick={() => handleSort(sort.key)}
                  aria-label={`Sort ${sort.dir === "asc" ? "ascending" : "descending"} — click to reverse`}
                  title="Reverse order"
                >
                  <FontAwesomeIcon icon={sort.dir === "asc" ? faArrowUpShortWide : faArrowDownWideShort} />
                </button>
              </div>
            </div>

            <TriageQueueTable
              groups={workflowGroups}
              queueView={isAssignedView ? "active" : queueView}
              layout={isAssignedView ? "assigned" : "triage"}
              showBucketBadge={!isAssignedView && bucket === "all"}
              onViewClient={openClient}
              onAssignRow={setAssignRow}
              onRowExpand={handleRowExpand}
              currentUser={bannerProps.currentUser}
              onSubmitMessage={handleSubmitMessage}
              onRequestClose={(row, note) => setCloseRow({ row, note })}
              onRequestReopen={handleReopenCase}
              onExploreInterventions={openSimulator}
              sort={sort}
              onSort={handleSort}
              onlySection={queueView === "active" ? activeTab : null}
            />
          </div>
          {assignRow ? (
            <AssignFollowUpPanel
              row={assignRow}
              locationId={locationId}
              onClose={() => setAssignRow(null)}
              onAssign={handleAssign}
              saving={assignSaving}
            />
          ) : null}
          {closeRow ? (
            <CloseCasePanel
              row={closeRow.row}
              initialMessage={closeRow.note}
              onClose={() => setCloseRow(null)}
              onConfirm={handleCloseCase}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default CareTriagePage;
